import type { Vec2 } from '../../geo/types';
import type { DensifyConfig, SurfaceRefinement } from './densify';
import { cachedHeight } from './heightCache';

/** A visible paving surface should follow the walking profile within a few centimetres. Painter-view
 * refinement alone can leave a crowned street as two flat triangles between its level edges. */
export const HEIGHT_MESH_ERROR_M = 0.025;
// Leave room for Float32 coordinates and interpolation in the final mesh.
const MIN_EDGE_M = 0.025;
const EDGE_PROBE_SPACING_M = 0.25;
const INTERIOR_SAMPLES = [
  [1 / 3, 1 / 3, 1 / 3],
  [0.5, 0.5, 0],
  [0, 0.5, 0.5],
  [0.5, 0, 0.5],
  [0.5, 0.25, 0.25],
  [0.25, 0.5, 0.25],
  [0.25, 0.25, 0.5],
] as const;

function heightSurface(
  vertexHeight: (p: Vec2) => number,
  fieldHeight: (p: Vec2) => number,
  sampleError: number
): SurfaceRefinement {
  return {
    tileSize: 8,
    withVertexHeight: (height) => heightSurface(height, fieldHeight, sampleError),
    needsRefinement(a, b, c) {
      if (
        Math.max(
          Math.hypot(b[0] - a[0], b[1] - a[1]),
          Math.hypot(c[0] - b[0], c[1] - b[1]),
          Math.hypot(a[0] - c[0], a[1] - c[1])
        ) <= MIN_EDGE_M
      )
        return false;
      const ya = vertexHeight(a),
        yb = vertexHeight(b),
        yc = vertexHeight(c);
      for (const [wa, wb, wc] of INTERIOR_SAMPLES) {
        const p: Vec2 = [a[0] * wa + b[0] * wb + c[0] * wc, a[1] * wa + b[1] * wb + c[1] * wc];
        const linear = ya * wa + yb * wb + yc * wc;
        if (Math.abs(fieldHeight(p) - linear) > sampleError) return true;
      }
      return false;
    },
  };
}

export function withHeightRefinement(
  base: DensifyConfig | undefined,
  sampleHeight: (p: Vec2) => number,
  maxError = HEIGHT_MESH_ERROR_M
): { readonly config: DensifyConfig; readonly heightAt: (p: Vec2) => number } {
  const heightAt = cachedHeight(sampleHeight),
    sampleError = maxError - 0.0002,
    // Almost collinear clipped caps interpolate between two nearby boundary chords. Keeping
    // those chords within half the surface allowance leaves room for their combined error.
    edgeError = sampleError / 2;
  return {
    heightAt,
    config: {
      ...base,
      zone: {
        // Depend on this edge alone, so ground and paving give their shared edge the same vertices.
        longestEdge(a, b) {
          const limit = base?.zone?.longestEdge(a, b) ?? Infinity;
          const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
          if (length <= MIN_EDGE_M) return limit;
          const ya = heightAt(a),
            yb = heightAt(b),
            steps = Math.ceil(length / EDGE_PROBE_SPACING_M);
          // Fractional probes alone can miss a short cross-street crown on a long chord.
          // Physical spacing makes the sampling density independent of the edge length.
          for (let step = 1; step < steps; step++) {
            const share = step / steps,
              p: Vec2 = [a[0] + (b[0] - a[0]) * share, a[1] + (b[1] - a[1]) * share];
            if (Math.abs(heightAt(p) - (ya + (yb - ya) * share)) > edgeError)
              return Math.min(limit, length / 2);
          }
          for (const share of [0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875]) {
            if (Number.isInteger(share * steps)) continue;
            const p: Vec2 = [a[0] + (b[0] - a[0]) * share, a[1] + (b[1] - a[1]) * share];
            if (Math.abs(heightAt(p) - (ya + (yb - ya) * share)) > edgeError)
              return Math.min(limit, length / 2);
          }
          return limit;
        },
      },
      surface: heightSurface(heightAt, sampleHeight, sampleError),
    },
  };
}
